import { startTransition, useId, useState } from 'react'
import { clsx } from 'clsx'
import { sv } from 'date-fns/locale'
import { DayPicker, type SelectSingleEventHandler } from 'react-day-picker'
import { useTranslation } from 'react-i18next'
import { formatAPIDate } from '@/utils/date'
import { Language } from '@/utils/l10n/types'
import { useCurrentLocale } from '@/utils/l10n/useCurrentLocale'
import { useFormatter } from '@/utils/useFormatter'
import * as Popover from '@radix-ui/react-popover'
import { LoadingDots } from 'ui/src/components/LoadingDots/LoadingDots'
import { Text } from 'ui/src/components/Text/Text'
import { ChevronIcon } from 'ui/src/icons/Chevron'
import { LockIcon } from 'ui/src/icons/LockIcon'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { tokens } from 'ui/src/theme/tokens.css'
import { useHighlightAnimation } from 'ui/src/utils/useHighlightAnimation'
import 'react-day-picker/dist/style.css'
import { chevronIcon, dayPicker, iconWrapper, popoverContent, trigger } from './InputDay.css'

export type InputDayProps = {
  label: string
  size?: 'small' | 'medium' | 'large'
  id?: string
  name?: string
  selected?: Date
  defaultSelected?: Date
  onSelect?: (day: Date) => void
  fromDate?: Date
  toDate?: Date
  disabled?: boolean
  required?: boolean
  autoFocus?: boolean
  loading?: boolean
  className?: string
  /** When true, shows chevron instead of lock icon when disabled */
  hideLockIcon?: boolean
}

export function InputDay(props: InputDayProps) {
  const { t } = useTranslation()
  const formatter = useFormatter()
  const { language } = useCurrentLocale()

  const [open, setOpen] = useState(props.autoFocus ?? false)
  const [value, setValue] = useState(props.selected ?? props.defaultSelected)

  // On its controlled form, InputDay needs to update its internal value whenever the selected prop changes.
  // This follows React's recommended way for such cases where you need to adjust some state when a prop changes
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const isControlled = props.selected !== undefined
  if (isControlled && props.selected !== value) {
    setValue(props.selected)
  }

  const autoIdentifier = useId()
  const inputId = props.id ?? autoIdentifier

  const shouldBeDisabled = props.disabled || props.loading

  const { highlight, animationProps } = useHighlightAnimation<HTMLButtonElement>()

  const handleSelect: SelectSingleEventHandler = (day) => {
    if (day) {
      setValue(day)
      props.onSelect?.(day)
    }
    highlight()
    setOpen(false)
  }

  const handleOpenChange = (open: boolean) => {
    if (open && shouldBeDisabled) return
    startTransition(() => {
      setOpen(open)
    })
  }

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger
        {...animationProps}
        className={clsx(trigger[props.size ?? 'medium'], props.className)}
        disabled={shouldBeDisabled}
        data-loading={props.loading}
      >
        <div>
          <label htmlFor={inputId}>
            <Text
              as="span"
              size="xs"
              color={shouldBeDisabled ? 'textTertiary' : 'textTranslucentSecondary'}
              singleLine={true}
            >
              {props.label}
            </Text>
          </label>
          <Text size="md" color={!value || shouldBeDisabled ? 'textTertiary' : 'textPrimary'}>
            {value ? formatter.fromNow(value) : t('DATE_INPUT_EMPTY_LABEL')}
          </Text>
        </div>

        <div className={iconWrapper}>
          {props.loading ? (
            <LoadingDots color={tokens.colors.fills.opaque.primary} />
          ) : props.disabled && !props.hideLockIcon ? (
            <LockIcon
              size="1rem"
              color={tokens.colors.fills.opaque.secondary}
              className={clsx(sprinkles({ margin: 'xxs' }))}
            />
          ) : (
            <ChevronIcon
              color={props.disabled ? tokens.colors.fills.opaque.tertiary : tokens.colors.fills.opaque.primary}
              className={clsx(chevronIcon.animated, sprinkles({ margin: 'xxs' }))}
              size="1rem"
            />
          )}
        </div>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content className={popoverContent} side="bottom" align="start" sideOffset={-4} alignOffset={-4}>
          <DayPicker
            className={dayPicker}
            mode="single"
            captionLayout="dropdown"
            locale={language === Language.Sv ? sv : undefined}
            required={props.required}
            selected={value}
            onSelect={handleSelect}
            defaultMonth={value}
            startMonth={props.fromDate}
            endMonth={props.toDate}
            disabled={[
              ...(props.fromDate ? [{ before: props.fromDate }] : []),
              ...(props.toDate ? [{ after: props.toDate }] : []),
            ]}
            weekStartsOn={1}
            components={{
              Chevron(props) {
                const orientation = props.orientation ?? 'down'
                return (
                  <ChevronIcon
                    className={chevronIcon[orientation]}
                    size={['up', 'down'].includes(orientation) ? '0.65rem' : '1rem'}
                  />
                )
              },
            }}
          />
        </Popover.Content>
      </Popover.Portal>

      {/* Hidden input for form submission */}
      <input
        id={inputId}
        name={props.name}
        // Use empty string to avoid switching back and forth between controlled and uncontrolled states
        value={value ? formatAPIDate(value) : ''}
        // Allows us to set value without an onChange handler
        readOnly
        // Hides the input visually and from the a11y tree
        hidden
        disabled={props.disabled}
        // Even though we pass 'required' to 'useInput' 'inputProps' props bag doesn't include
        // it so we need to add it manually here.
        required={props.required}
      />
    </Popover.Root>
  )
}
